import React, { useState, useEffect } from "react";
import classNames from "classnames";
import localforage from "localforage";
import { useStoreState } from "pullstate";

import { apiPath } from "shared/helpers/apiPath";
import client from "@/helpers/apiClient";
import RemoveIcon from "@/icons/feedRemove.svg";
import RemoveIconLarge from "@/icons/feedRemoveLarge.svg";
import AddIcon from "@/icons/feedAdd.svg";
import AddIconLarge from "@/icons/feedAddLarge.svg";
import AddIcon20 from "@/icons/addToFeed20.svg";
import RemoveIcon20 from "@/icons/removeFromFeed20.svg";
import IconButton from "@/components/IconButton";
import ActionsModal from "@/components/Modals/ActionsModal";
import Typography from "@/components/Typography";
import Checkbox from "@/components/Checkbox";
import {
  CheckboxEnum,
  FollowAuthorButtonInteface,
} from "@/components/FollowAuthorButton/FollowAuthorButton.inteface";
import { UIStore } from "@/store/UIStore";
import { useDataLayer } from "@/helpers/useDataLayer";

import styles from "./FollowAuthorButton.module.scss";

export const FollowAuthorButton: React.FC<FollowAuthorButtonInteface> = ({
  followed,
  slug,
  className,
  variant,
  size,
}) => {
  const { userLoggedIn } = useStoreState(UIStore, (store) => store);
  const { pushToDataLayer } = useDataLayer();
  const [isFollowed, setFollowed] = useState<boolean>(followed);
  const [isOpen, setOpen] = useState<boolean>(false);

  useEffect(() => {
    setFollowed(followed);
  }, [followed]);

  const handleFollow = async () => {
    try {
      isFollowed
        ? await client.delete(apiPath.REMOVE_FOLLOW_AUTHOR(slug))
        : await client
            .post(apiPath.FOLLOW_AUTHOR, {
              slug,
            })
            .then(
              !isFollowed &&
                window?.fbq("trackCustom", "Follow author", { name: slug })
            );
      setFollowed((prevState) => {
        const eventName = !prevState ? "follow_author" : "unfollow_author";
        pushToDataLayer(eventName, { author: slug });
        return !prevState;
      });
      setOpen(false);
    } catch (e) {
      //TODO: add error handling
    }
  };

  const handleModalState = () => {
    setOpen((prevState) => !prevState);
  };

  const handleClick = () => {
    const addConfirmation =
      localforage.getItem(CheckboxEnum.AddConfirmation) || false;
    const removeConfirmation =
      localforage.getItem(CheckboxEnum.RemoveConfirmation) || false;

    if (!userLoggedIn) {
      return null;
    }

    if (isFollowed) {
      if (removeConfirmation) {
        return handleFollow();
      } else {
        return handleModalState();
      }
    } else {
      if (addConfirmation) {
        return handleFollow();
      } else {
        return handleModalState();
      }
    }
  };

  const handleCheckboxChange = async (e: any) => {
    await localforage.setItem(e.target.name, e.target.checked);
  };

  const FollowIcon = () => {
    if (variant === "round") {
      return <AddIcon20 />;
    }

    return <AddIcon />;
  };

  const UnfollowIcon = () => {
    if (variant === "round") {
      return <RemoveIcon20 />;
    }

    return <RemoveIcon />;
  };

  return (
    <>
      <IconButton
        className={classNames(className, "followAuthor")}
        variant={variant}
        size={size}
        onClick={handleClick}
        active={isFollowed}
      >
        {isFollowed ? <UnfollowIcon /> : <FollowIcon />}
      </IconButton>

      <ActionsModal
        confirmActionHandler={handleFollow}
        confirmActionLabel="Tak"
        cancelActionLabel="Anuluj"
        onClose={handleModalState}
        isOpen={isOpen}
      >
        {isFollowed ? (
          <>
            <RemoveIconLarge className={styles.icon} />
            <Typography variant="h2" component="p">
              Usunięcie redaktora z obserwowanych spowoduje, że jego treści
              znikną z twojego spersonalizowanego feedu. Czy chcesz to zrobić?
            </Typography>

            <div className={styles.checkbox}>
              <Checkbox
                name={CheckboxEnum.RemoveConfirmation}
                onClick={handleCheckboxChange}
              />

              <Typography
                variant="small"
                component="p"
                className={styles.label}
              >
                Nie pytaj ponownie
              </Typography>
            </div>
          </>
        ) : (
          <>
            <AddIconLarge className={styles.icon} />
            <Typography variant="h2" component="p">
              Obserwowanie redaktora spowoduje, że wszystkie jego treści będą
              wpadać do twojego spersonalizowanego feedu. Czy chcesz to zrobić?
            </Typography>
            <div className={styles.checkbox}>
              <Checkbox
                name={CheckboxEnum.AddConfirmation}
                onClick={handleCheckboxChange}
              />

              <Typography
                variant="small"
                component="p"
                className={styles.label}
              >
                Nie pytaj ponownie
              </Typography>
            </div>
          </>
        )}
      </ActionsModal>
    </>
  );
};

export default FollowAuthorButton;
